/**
 * Agregación de deudas para el dashboard ejecutivo.
 * Fuente: GET /ventas/cuentas-por-cobrar (saldo_pendiente por venta).
 */

export const DEFAULT_DEUDA_VENCIDA_DIAS = 30;

export function normalizarDeudaVencidaDias(raw) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return DEFAULT_DEUDA_VENCIDA_DIAS;
  return Math.min(365, Math.max(0, n));
}

export function calcularDiasAtraso(fecha, hoy = new Date()) {
  if (!fecha) return 0;
  const f = new Date(fecha);
  if (Number.isNaN(f.getTime())) return 0;
  return Math.max(0, Math.floor((hoy - f) / (1000 * 60 * 60 * 24)));
}

export function parseCuentasPorCobrarResponse(raw) {
  if (!raw) return { por_cliente: [], ventas: [], resumen: {} };
  if (Array.isArray(raw)) {
    return { por_cliente: [], ventas: raw, resumen: {} };
  }
  if (Array.isArray(raw.por_cliente)) {
    return {
      por_cliente: raw.por_cliente,
      ventas: Array.isArray(raw.ventas) ? raw.ventas : [],
      resumen: raw.resumen || {}
    };
  }
  if (raw.data) {
    if (Array.isArray(raw.data)) {
      return { por_cliente: [], ventas: raw.data, resumen: {} };
    }
    if (Array.isArray(raw.data.por_cliente) || Array.isArray(raw.data.ventas)) {
      return {
        por_cliente: Array.isArray(raw.data.por_cliente) ? raw.data.por_cliente : [],
        ventas: Array.isArray(raw.data.ventas) ? raw.data.ventas : [],
        resumen: raw.data.resumen || {}
      };
    }
  }
  if (Array.isArray(raw.ventas)) {
    return { por_cliente: [], ventas: raw.ventas, resumen: raw.resumen || {} };
  }
  return { por_cliente: [], ventas: [], resumen: raw.resumen || {} };
}

/**
 * @param {object} cuentasData - respuesta normalizada de cuentas-por-cobrar
 * @param {number} deudaVencidaDias
 * @returns {{ diasCriterio: number, resumen: object, clientes: object[] }}
 */
export function buildResumenDeudaEjecutivo(cuentasData, deudaVencidaDias = DEFAULT_DEUDA_VENCIDA_DIAS) {
  const diasCriterio = normalizarDeudaVencidaDias(deudaVencidaDias);
  const porCliente = Array.isArray(cuentasData?.por_cliente) ? cuentasData.por_cliente : [];

  const clientes = porCliente
    .filter((row) => row.cliente_id && (parseFloat(row.total_pendiente) || 0) > 0)
    .map((row) => {
      const ventas = Array.isArray(row.ventas) ? row.ventas : [];
      let montoVencido = 0;
      let fechaMasAntigua = null;
      let maxDiasAtraso = 0;

      ventas.forEach((venta) => {
        const saldo = parseFloat(venta.saldo_pendiente) || 0;
        if (saldo <= 0 || venta.estado === 'cancelada') return;
        const dias = calcularDiasAtraso(venta.fecha);
        if (dias >= diasCriterio) montoVencido += saldo;
        if (dias > maxDiasAtraso) maxDiasAtraso = dias;
        const f = venta.fecha ? new Date(venta.fecha) : null;
        if (f && !Number.isNaN(f.getTime()) && (!fechaMasAntigua || f < fechaMasAntigua)) {
          fechaMasAntigua = f;
        }
      });

      const info = row.cliente_info || {};
      const nombre = [info.nombre, info.apellido].filter(Boolean).join(' ').trim()
        || info.nombre_completo
        || `Cliente ${String(row.cliente_id).slice(0, 8)}`;

      return {
        id: row.cliente_id,
        nombre,
        totalDeuda: parseFloat(row.total_pendiente) || 0,
        montoVencido,
        fechaDeudaMasAntigua: fechaMasAntigua ? fechaMasAntigua.toISOString() : null,
        diasAtraso: maxDiasAtraso,
        cantidadVentas: row.cantidad_ventas || ventas.length
      };
    })
    .sort((a, b) => b.montoVencido - a.montoVencido || b.totalDeuda - a.totalDeuda);

  const resumenBackend = cuentasData?.resumen || {};
  const totalAdeudado = parseFloat(resumenBackend.total_pendiente)
    || clientes.reduce((sum, c) => sum + c.totalDeuda, 0);
  const totalVencido = clientes.reduce((sum, c) => sum + c.montoVencido, 0);

  return {
    diasCriterio,
    resumen: {
      cantidadClientes: clientes.length,
      totalAdeudado,
      totalVencido
    },
    clientes
  };
}
